import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, TrendingUp, Search as SearchIcon, Megaphone, X, Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AnnouncementCallsPanel from "@/components/AnnouncementCallsPanel";
import EditStudentDialog from "@/components/EditStudentDialog";
import StudentDetailsDialog from "@/components/StudentDetailsDialog";
import PresenceDot from "@/components/PresenceDot";
import ExportDialog from "@/components/ExportDialog";
import { presenceTooltip } from "@/lib/presence";

const CLASSES = ["Play Group","LKG","UKG","1","2","3","4","5","6","7","8","9","10"];
const SECTIONS = ["A","B"];

export default function Students() {
  const { user } = useAuth();
  const firstAssignment = user?.assignments?.[0];
  const [classSel, setClassSel] = useState(firstAssignment?.class_name || "5");
  const [secSel, setSecSel] = useState(firstAssignment?.section || "A");
  const [classMeta, setClassMeta] = useState({ classes: [], assignments: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [announceOpen, setAnnounceOpen] = useState(false);

  const canAnnounce = user?.role === "admin" || user?.role === "teacher";
  const canEdit = user?.role === "admin" || user?.role === "teacher";
  const canDelete = user?.role === "admin";
  const [editingStudent, setEditingStudent] = useState(null);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const bulkDelete = async () => {
    const ids = [...selected];
    setBulkDeleting(true);
    let ok = 0;
    for (const id of ids) {
      try { await api.delete(`/admin/students/${id}`); ok += 1; } catch { /* skip */ }
    }
    setBulkDeleting(false);
    setData((d) => d ? { ...d, students: d.students.filter((x) => !selected.has(x.id)) } : d);
    setSelected(new Set());
    toast.success(`${ok} student${ok === 1 ? "" : "s"} deleted`);
  };
  // {studentId: {status, last_seen_at}} — refreshed every 30s
  const [presence, setPresence] = useState({});

  useEffect(() => {
    api.get("/meta/classes").then(({ data }) => setClassMeta(data)).catch(() => {});
  }, []);

  // Class/section options scoped to the teacher's assignments (admins get all).
  const classOptions = classMeta.classes?.length ? classMeta.classes : [classSel];
  const sectionOptions = (() => {
    const secs = (classMeta.assignments || [])
      .filter((a) => a.class_name === classSel)
      .map((a) => a.section);
    const uniq = [...new Set(secs)].sort();
    return uniq.length ? uniq : (secSel ? [secSel] : []);
  })();
  useEffect(() => {
    if (sectionOptions.length && !sectionOptions.includes(secSel)) setSecSel(sectionOptions[0]);
    // eslint-disable-next-line
  }, [classSel, classMeta]);

  useEffect(() => { setSelected(new Set()); }, [classSel, secSel]);

  // Live presence polling — refreshes every 30s when class/section changes or stays
  useEffect(() => {
    if (!classSel || !secSel) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/student-presence", { params: { class_name: classSel, section: secSel } });
        if (!cancelled) setPresence(data || {});
      } catch { /* silent */ }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [classSel, secSel]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/students/list", { params: { class_name: classSel, section: secSel } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load students");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [classSel, secSel]);

  const filtered = (data?.students || []).filter((s) =>
    !q.trim() || s.name.toLowerCase().includes(q.toLowerCase()) || s.roll_number.toLowerCase().includes(q.toLowerCase()) || String(s.student_id || "").includes(q.trim())
  );

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2">Roster</div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Students</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Pick a class and section to see roster, attendance, and last-test totals.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <FilterBox label="Class">
          <Select value={classSel} onValueChange={setClassSel}>
            <SelectTrigger className="h-11" data-testid="students-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>{classOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </FilterBox>
        <FilterBox label="Section">
          <Select value={secSel} onValueChange={setSecSel}>
            <SelectTrigger className="h-11" data-testid="students-section-select"><SelectValue /></SelectTrigger>
            <SelectContent>{sectionOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </FilterBox>
        <FilterBox label="Search">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, roll no, or Student ID"
              className="h-11 pl-9"
              data-testid="students-search-input"
            />
          </div>
        </FilterBox>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden" data-testid="students-table">
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700 text-xs uppercase tracking-[0.15em] text-slate-500 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Class {classSel} · Section {secSel}
            {(() => {
              const online = Object.values(presence).filter((p) => p.status === "online").length;
              const away = Object.values(presence).filter((p) => p.status === "away").length;
              if (online + away === 0) return null;
              return (
                <span className="ml-2 normal-case tracking-normal text-xs font-medium text-slate-500 inline-flex items-center gap-1" data-testid="students-online-count">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  {online} online{away > 0 ? ` · ${away} away` : ""}
                </span>
              );
            })()}
            {selected.size > 0 && (
              <span className="ml-2 normal-case tracking-normal text-xs font-semibold bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 px-2 py-0.5 rounded-full" data-testid="students-picked-count">
                {selected.size} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <ExportDialog entity="students" params={{ class_name: classSel, section: secSel }} disabled={!classSel || !secSel} buttonLabel="Export Excel" />
            )}
            {canAnnounce && selected.size > 0 && (
              <Button
                onClick={() => setAnnounceOpen(true)}
                className="h-8 px-3 rounded-lg bg-violet-500 hover:bg-violet-600 text-white text-xs normal-case tracking-normal"
                data-testid="students-announce-btn"
              >
                <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                Call parents ({selected.size})
              </Button>
            )}
            {canDelete && selected.size > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    disabled={bulkDeleting}
                    className="h-8 px-3 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs normal-case tracking-normal"
                    data-testid="students-bulk-delete-btn"
                  >
                    {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
                    Delete ({selected.size})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="bulk-delete-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {selected.size} student{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes the selected students and their attendance, marks and tutor-session history. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={bulkDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="bulk-delete-confirm-btn">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {selected.size > 0 && (
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="text-slate-400 hover:text-rose-500 normal-case tracking-normal"
                data-testid="students-clear-selection"
                title="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <div>
              {data?.last_exam_label
                ? <>Last test: <span className="font-semibold text-slate-700 dark:text-slate-300">{data.last_exam_label}</span></>
                : <span className="text-amber-600">No test results yet</span>}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="students-empty">
            No students {q ? "match your search" : "in this section"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700 text-left">
                  {canAnnounce && (
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-violet-500"
                        checked={filtered.length > 0 && filtered.every((s) => selected.has(s.id))}
                        onChange={() => {
                          const allOn = filtered.every((s) => selected.has(s.id));
                          setSelected((prev) => {
                            const next = new Set(prev);
                            filtered.forEach((s) => allOn ? next.delete(s.id) : next.add(s.id));
                            return next;
                          });
                        }}
                        data-testid="students-toggle-all"
                        aria-label="Select all"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 w-28 text-base">Student ID</th>
                  <th className="px-4 py-3 w-16 text-base">Roll</th>
                  <th className="px-4 py-3">Student</th>
                  <th className="px-4 py-3 text-center">Attendance (30d)</th>
                  <th className="px-4 py-3 text-center">{data?.last_exam_label || "Last test"} · Total</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, idx) => {
                  const att = s.attendance_pct;
                  const attTone = att == null ? "slate" : att >= 90 ? "emerald" : att >= 75 ? "sky" : "rose";
                  const isSelected = selected.has(s.id);
                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-slate-100 dark:border-slate-800 ${isSelected ? "bg-violet-50/60 dark:bg-violet-900/20" : idx % 2 ? "bg-slate-50/40 dark:bg-slate-900/20" : ""}`}
                      data-testid={`student-row-${s.roll_number}`}
                    >
                      {canAnnounce && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-violet-500"
                            checked={isSelected}
                            onChange={() => setSelected((prev) => {
                              const next = new Set(prev);
                              if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                              return next;
                            })}
                            data-testid={`student-check-${s.roll_number}`}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 font-mono text-sm text-slate-600 dark:text-slate-300" data-testid={`student-sid-${s.roll_number}`}>{s.student_id || "—"}</td>
                      <td className="px-4 py-3 font-mono text-sm text-slate-500">{s.roll_number}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="relative">
                            {s.has_photo ? (
                              <img src={`${apiBase}/profile/photo/${s.id}`} className="h-9 w-9 rounded-xl object-cover" alt={s.name} />
                            ) : (
                              <div className="h-9 w-9 rounded-xl bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 flex items-center justify-center text-xs font-semibold">
                                {s.name.split(" ").map((p) => p[0]).slice(0,2).join("")}
                              </div>
                            )}
                            {/* Presence dot on the corner of the avatar */}
                            <span className="absolute -bottom-0.5 -right-0.5 inline-flex p-0.5 rounded-full bg-white dark:bg-slate-800">
                              <PresenceDot
                                status={presence[s.id]?.status || "offline"}
                                lastSeenAt={presence[s.id]?.last_seen_at}
                              />
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setViewingStudent(s)}
                            title={presenceTooltip({ status: presence[s.id]?.status, lastSeenAt: presence[s.id]?.last_seen_at, lastLoginAt: presence[s.id]?.last_login_at })}
                            className="font-medium text-slate-900 dark:text-slate-100 text-left hover:text-sky-600 dark:hover:text-sky-300 hover:underline underline-offset-2 transition-colors"
                            data-testid={`student-name-${s.roll_number}`}
                          >
                            {s.name}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {att == null ? (
                          <span className="text-xs text-slate-300">—</span>
                        ) : (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                            attTone === "emerald" ? "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300" :
                            attTone === "sky" ? "bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300" :
                            "bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300"
                          }`}><TrendingUp className="h-3 w-3" />{att}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {s.marks_subjects > 0 ? (
                          <div>
                            <div className="font-semibold text-slate-900 dark:text-slate-100">{s.marks_total} <span className="text-xs text-slate-400 font-normal">/ {s.marks_out_of}</span></div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.marks_subjects} subject{s.marks_subjects > 1 ? "s" : ""}</div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canAnnounce && (
        <AnnouncementCallsPanel
          isOpen={announceOpen}
          onClose={() => setAnnounceOpen(false)}
          defaultClass={classSel}
          defaultSection={secSel}
          presetStudents={(data?.students || []).filter((s) => selected.has(s.id))}
          isTeacher={user?.role === "teacher"}
        />
      )}
      {canEdit && (
        <EditStudentDialog
          open={!!editingStudent}
          onClose={() => setEditingStudent(null)}
          student={editingStudent}
          onSaved={(updated) => {
            // Patch the row in-place so the table reflects new name/parent fields immediately
            setData((d) => d ? { ...d, students: d.students.map((x) => x.id === updated.id ? { ...x, ...updated } : x) } : d);
          }}
          onDeleted={(id) => {
            setData((d) => d ? { ...d, students: d.students.filter((x) => x.id !== id) } : d);
          }}
        />
      )}
      <StudentDetailsDialog
        open={!!viewingStudent}
        onClose={() => setViewingStudent(null)}
        student={viewingStudent}
        canEdit={canEdit}
        onEdit={(s) => { setViewingStudent(null); setEditingStudent(s); }}
      />
    </Layout>
  );
}

function FilterBox({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
